"use client"

import { ConfigJSON, ConfigJSONBase } from "@tc/configurability/types"
import {
  getActiveTemplateBySubmissionLayout,
  getDfeFormConfig,
  getTemplateWithApprovedVersion,
  SubmissionLayoutType,
} from "@tc/ui-dfe/query"
import useCloudWatch from "@tc/ui-rum/hooks/useCloudWatch"
import { useClient } from "@urql/next"
import { useParams, usePathname } from "next/navigation"
import { createContext, useContext, useEffect, useState } from "react"
import { RequestType } from "../config/helper"
import { CertificateType } from "@tc/graphql-server"

export interface RecipeProviderProps {
  children: React.ReactNode
  requestType: RequestType
  templateInfo?: {
    templateId: string
    templateVersionId: string
  }
}

export interface RecipeContextProps {
  requestType: RequestType
  recipeId?: ConfigJSONBase
  setRecipeId?: (recipeId: ConfigJSONBase) => void
  recipe?: ConfigJSON
  getRecipeField?: (fuid: string) => ConfigJSON["fields"][number] | undefined
  templateCode?: string
  setTemplateCode?: (templateCode: string | undefined) => void
  isPhyto?: boolean
  isAPExport?: boolean
  templateTitle?: string
  isNonConsignmentRequest?: boolean
  hasDeclarationFields?: boolean
  hasProductFields?: boolean
}

const RecipeContext = createContext<RecipeContextProps>({
  requestType: "transfer",
})

export const RecipeProvider = ({
  requestType,
  templateInfo,
  children,
}: RecipeProviderProps) => {
  const [templateCode, setTemplateCode] = useState<string | undefined>()
  const [templateTitle, setTemplateTitle] = useState<string | undefined>()
  const [isNonConsignmentRequest, setIsNonConsignmentRequest] = useState<
    boolean | undefined
  >()
  const [recipeId, setRecipeId] = useState<ConfigJSONBase | undefined>()
  const [recipe, setRecipe] = useState<ConfigJSON | undefined>()
  const [recipeFields, setRecipeFields] = useState<
    Record<string, ConfigJSON["fields"][number]>
  >({})
  const pathname = usePathname()
  const { logError } = useCloudWatch()
  const params = useParams()
  const client = useClient()
  const isPhyto =
    recipe?.meta.submissionLayout === SubmissionLayoutType.PhytoExport
  const isAPExport =
    recipe?.meta.submissionLayout === SubmissionLayoutType.ApExport

  // Check if declaration fields are configured in the recipe (for non-consignment requests)
  const hasDeclarationFields =
    (recipe?.declarationGroups?.flatMap((g) => g.statements ?? []).length ??
      0) > 0

  // Check if product fields are configured in the recipe (for non-consignment requests)
  const hasProductFields =
    (recipe?.products?.sections?.flatMap((s) =>
      (s.children ?? []).flatMap((c) => c.rows ?? []),
    ).length ?? 0) > 0

  // Detect readonly mode based on URL path
  const isReadonlyMode = pathname?.includes("/view/")

  useEffect(() => {
    fetchRecipeInfo()
  }, [requestType, templateInfo, pathname])

  useEffect(() => {
    fetchDfeRecipe()
  }, [recipeId?.templateId, recipeId?.templateRevisionId, requestType])

  const fetchRecipeInfo = async () => {
    if (requestType === "transfer") {
      // Check if this is a transfer request page (has transfer ID in URL)
      const hasTransferRequestId =
        pathname?.includes("/transfer/") &&
        !pathname?.includes("/transfer/start") &&
        (new RegExp(/\/transfer\/[^/]+$/).exec(pathname) ||
          pathname?.includes("/view/"))

      if (hasTransferRequestId) {
        // Don't set recipe - let transfer request hook handle it completely
        return
      }

      // Always fetch active template for banner logic, but don't set recipe for readonly mode
      const { data } = await client.query(
        getActiveTemplateBySubmissionLayout.toString(),
        { submissionLayout: SubmissionLayoutType.ApTransfer },
      )

      if (data?.getActiveTemplateBySubmissionLayout) {
        const { templateId, templateVersionId } =
          data.getActiveTemplateBySubmissionLayout

        // Only set recipe for edit mode - readonly mode will be handled by transfer request hook
        if (!isReadonlyMode && templateId && templateVersionId) {
          setRecipeId({ templateId, templateRevisionId: templateVersionId })
        }
      }

      // Handle provided template info (legacy support)
      if (
        templateInfo?.templateId &&
        templateInfo?.templateVersionId &&
        !isReadonlyMode
      ) {
        setRecipeId({
          templateId: templateInfo.templateId,
          templateRevisionId: templateInfo.templateVersionId,
        })
      }
    }
  }

  const fetchDfeRecipe = async () => {
    const templateId = recipeId?.templateId ?? (params?.templateId as string)
    let templateVersionId = recipeId?.templateRevisionId

    if (templateId && !templateVersionId) {
      try {
        const { data } = await client.query(
          getTemplateWithApprovedVersion.toString(),
          { templateId },
        )
        templateVersionId =
          data?.getTemplateWithActiveApproved?.approvedVersion?.id
      } catch (error) {
        console.error("Failed to fetch approved template version:", error)
      }
    }

    if (templateId && templateVersionId) {
      if (!recipeId?.templateRevisionId) {
        setRecipeId({ templateId, templateRevisionId: templateVersionId })
      }

      const { data } = await client.query(getDfeFormConfig.toString(), {
        templateId,
        templateVersionId,
      })

      processRecipeData(data)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processRecipeData = (data: any) => {
    const config = data?.getDfeForm?.config
    if (config) {
      try {
        const parsedJson = JSON.parse(config)

        // Filter keys based on catalogue
        if (parsedJson.meta?.catalogue) {
          const catalogue = new Set(parsedJson.meta.catalogue)

          Object.keys(parsedJson).forEach((key) => {
            if (
              key !== "meta" &&
              key !== "templateId" &&
              key !== "templateRevisionId" &&
              !catalogue.has(key)
            ) {
              delete parsedJson[key]
            }
          })
        }

        // Remove certifying questions from products section for export requests
        if (
          parsedJson.meta?.certificateType === "PHYTOSANITARY" &&
          parsedJson.products?.sections
        ) {
          parsedJson.products.sections = parsedJson.products.sections.map(
            (section: { children?: Array<{ title?: string }> }) => ({
              ...section,
              children:
                section.children?.filter(
                  (block: { title?: string }) =>
                    !block.title
                      ?.toLowerCase()
                      .includes("certifying questions"),
                ) || [],
            }),
          )
        }

        const fieldsMap = parsedJson.fields
          ? parsedJson.fields.reduce(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (acc: any, field: any) => {
                acc[field.fuid] = field
                return acc
              },
              {} as Record<string, ConfigJSON["fields"][number]>,
            )
          : {}

        setRecipe(parsedJson)
        setRecipeFields(fieldsMap)
        setTemplateCode(parsedJson?.meta?.code)
        setTemplateTitle(parsedJson?.meta?.title)
        setIsNonConsignmentRequest(
          parsedJson?.meta?.certificateType === CertificateType.Nonconsignment,
        )
      } catch (e) {
        logError("Error in processRecipeData:", e)
        throw new Error("Error occurred while parsing the recipe config json")
      }
    }
  }

  const getRecipeField = (fuid: string) => {
    return recipeFields[fuid]
  }

  return (
    <RecipeContext.Provider
      value={{
        recipeId,
        recipe,
        requestType,
        getRecipeField,
        setRecipeId,
        templateCode,
        setTemplateCode,
        isPhyto,
        isAPExport,
        templateTitle,
        isNonConsignmentRequest,
        hasDeclarationFields,
        hasProductFields,
      }}
    >
      {children}
    </RecipeContext.Provider>
  )
}

export const useRecipe = () => {
  const context = useContext(RecipeContext)
  if (!context) {
    throw new Error("useRecipe must be used within a RecipeProvider")
  }
  return context
}
